/* eslint-disable max-lines */
import React, { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AttributeType } from "@evergis/api";
import { Divider, Flex, FlexSpan, IconButton, Popup, IconToggle, Checkbox } from "@evergis/ui";

import {
  LayerStatisticsContainer,
  LayerStatisticsHead,
  LayerStatisticsBody,
  LayerStatisticsTitle,
  LayerStatisticsPickAttributesMenu,
  VisibilityCheckboxLabel,
} from "./styled";

import { AttributesList } from "./AttributesList";

import { CENTROID_ATTRIBUTE, DEFAULT_ID_ATTRIBUTE_NAME, GEOMETRY_ATTRIBUTE } from "../../../../evergis/constants";

import { useSelectFeaturesStatisticsActive } from "../../../../hooks/selectFeatures/useSelectFeaturesStatisticsActive";
import { useSelectFeaturesLayerName } from "../../../../hooks/selectFeatures/useSelectFeaturesLayerName";
import { useToggle } from "../../../../hooks/useToggle";
import { usePostgresLayer } from "../../../../hooks/layer/usePostgresLayer";
import { useSelectedFeaturesCondition } from "./hooks/useSelectedFeaturesCondition";
import { useTempLayer } from "../../../../hooks/layer/useTempLayer";
import { useProjectUpdate } from "../../../../ducks/project/hooks";
import { useFeatureCard } from "../../hooks/useFeatureCard";
import { getClientData } from "../../../../ducks/project/selectors";
import { getAttributePrefixIcon } from "../../../../utils/getAttributePrefixIcon";
import { featuresCount } from "../../../../resources/features";

import { LayerStatisticsProps } from "./types";

const SYSTEM_ATTRIBUTES_NAMES = [GEOMETRY_ATTRIBUTE, DEFAULT_ID_ATTRIBUTE_NAME, CENTROID_ATTRIBUTE];

// eslint-disable-next-line max-lines-per-function
export const LayerStatistics: FC<LayerStatisticsProps> = ({ onCount }) => {
  const { t } = useTranslation("featureCard");
  const [, setSelectFeaturesStatisticsActive] = useSelectFeaturesStatisticsActive();
  const [selectFeaturesLayerName, setSelectFeaturesLayerName] = useSelectFeaturesLayerName();
  const [menuOpen, toggleMenuOpen] = useToggle(false);
  const { postgresLayer, loading: layerInfoLoading } = usePostgresLayer(selectFeaturesLayerName, { waitInput: true });
  const { getTempLayerInfo } = useTempLayer();
  const projectUpdate = useProjectUpdate();
  const clientData = useSelector(getClientData);
  const tempLayerInfo = getTempLayerInfo(selectFeaturesLayerName);
  const layerInfo = tempLayerInfo || postgresLayer;
  const layerAttributes = layerInfo?.layerDefinition?.attributes;
  const [visibility, setVisibility] = useState<Record<string, Record<string, boolean>>>({});
  const [, { closeCard }] = useFeatureCard();
  const processedCondition = useSelectedFeaturesCondition();

  const visibleCount = useMemo(
    () =>
      visibility[selectFeaturesLayerName]
        ? Object.entries(visibility[selectFeaturesLayerName]).reduce((acc, [, value]) => acc + Number(value), 0)
        : 0,
    [visibility[selectFeaturesLayerName]],
  );

  const attributesList = useMemo(() => {
    if (!layerAttributes) {
      return [];
    }

    return Object.entries(layerAttributes).reduce((acc, [key, value]) => {
      if (value.isDisplayed && key !== layerInfo?.geometryAttribute && !SYSTEM_ATTRIBUTES_NAMES.includes(key)) {
        const { alias, type, subType, stringFormat } = value;
        const header = alias || key;

        return [
          ...acc,
          {
            type,
            name: key,
            header,
            icon: getAttributePrefixIcon(key, type, subType),
            unit: stringFormat?.unitsLabel,
          },
        ];
      }

      return acc;
    }, []);
  }, [layerAttributes, layerInfo?.geometryAttribute]);

  const { data: count, refetch: refetchCount } = featuresCount.use({
    input: {
      name: selectFeaturesLayerName,
      condition: processedCondition,
    },
    immediate: false,
  });

  useEffect(() => {
    if (selectFeaturesLayerName && attributesList.length > 0 && visibility[selectFeaturesLayerName] === undefined) {
      setVisibility(prevState => ({
        ...prevState,
        [selectFeaturesLayerName]: attributesList.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.name]: [AttributeType.Int32, AttributeType.Int64, AttributeType.Double].includes(curr.type),
          }),
          {},
        ),
      }));
    }
  }, [attributesList, visibility, selectFeaturesLayerName]);

  useEffect(() => {
    if (selectFeaturesLayerName) {
      refetchCount();
    }
  }, [processedCondition, selectFeaturesLayerName]); // eslint-disable-line

  useEffect(() => {
    onCount?.(count);
  }, [count]);

  useEffect(() => {
    if (clientData) {
      setVisibility(clientData.statisticsVisibility ?? {});
    }
  }, [clientData]); // eslint-disable-line

  return (
    <LayerStatisticsContainer>
      <LayerStatisticsHead>
        <Flex style={{ alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
          <FlexSpan>
            <Flex style={{ gap: "0.5rem" }}>
              <FlexSpan>
                <LayerStatisticsTitle>{t("layerStatistics")}</LayerStatisticsTitle>
              </FlexSpan>
            </Flex>
          </FlexSpan>
          <FlexSpan>
            <Flex>
              <FlexSpan>
                <Popup
                  zIndex={101}
                  anchor={ref => (
                    <IconToggle kind="switcher" innerRef={ref} isSelected={menuOpen} onClick={toggleMenuOpen} />
                  )}
                  isVisible={menuOpen}
                  onRequestClose={async () => {
                    toggleMenuOpen();

                    await projectUpdate({
                      content: {
                        clientData: {
                          ...clientData,
                          statisticsVisibility: visibility,
                        },
                      },
                    });
                  }}
                >
                  <LayerStatisticsPickAttributesMenu>
                    <Checkbox
                      label={t("allAttributes")}
                      checked={visibleCount > 0}
                      indeterminate={visibleCount > 0 && visibleCount < attributesList.length}
                      onChange={() => {
                        setVisibility({
                          ...visibility,
                          [selectFeaturesLayerName]: Object.keys(visibility[selectFeaturesLayerName] ?? {}).reduce(
                            (acc, key) => ({
                              ...acc,
                              [key]: !(visibleCount === attributesList.length),
                            }),
                            {},
                          ),
                        });
                      }}
                    />
                    {attributesList.map(attribute => {
                      return (
                        <Checkbox
                          key={`layer-attributes-visibility-checkbox-${attribute.name}`}
                          label={
                            <Flex style={{ gap: "0.5rem", flexWrap: "nowrap" }}>
                              <FlexSpan>{attribute.icon}</FlexSpan>
                              <FlexSpan>
                                <VisibilityCheckboxLabel>{attribute.header}</VisibilityCheckboxLabel>
                              </FlexSpan>
                            </Flex>
                          }
                          onChange={() =>
                            setVisibility({
                              ...visibility,
                              [selectFeaturesLayerName]: {
                                ...visibility[selectFeaturesLayerName],
                                [attribute.name]: !visibility[selectFeaturesLayerName]?.[attribute.name],
                              },
                            })
                          }
                          checked={visibility[selectFeaturesLayerName]?.[attribute.name]}
                          name={attribute.name}
                          value={attribute.name}
                        />
                      );
                    })}
                  </LayerStatisticsPickAttributesMenu>
                </Popup>
              </FlexSpan>
              <FlexSpan>
                <IconButton
                  kind="close"
                  onClick={() => {
                    closeCard();

                    setSelectFeaturesStatisticsActive(false);
                    setSelectFeaturesLayerName(undefined);
                  }}
                />
              </FlexSpan>
            </Flex>
          </FlexSpan>
        </Flex>
      </LayerStatisticsHead>
      <Divider />
      <LayerStatisticsBody>
        <AttributesList
          attributes={attributesList}
          visibility={visibility[selectFeaturesLayerName]}
          loading={layerInfoLoading}
        />
      </LayerStatisticsBody>
    </LayerStatisticsContainer>
  );
};
